import { useAppSelector } from "app/redux-store-interactions";
import { FeatureToggle } from "config/featureToggles";
import { useIsPhone } from "contexts/panelMode";
import { selectCameraDefaults } from "features/render/renderSlice";
import { selectIsProcoreMobileDesign, selectIsSpaceMouseAvailable } from "slices/explorer";
import { useFeatureToggle } from "slices/featureToggles";

import { useSpaceMouseDriverConfirmed } from "./status";

export type SpaceMouseAccess = {
    /** User setting from Redux / localStorage. */
    settingOn: boolean;
    /** Host flag is on and this is a desktop viewport (Settings toggle is shown). */
    desktopAvailable: boolean;
    /** Default nav icon shows the SpaceMouse glyph: setting, desktop, and a driver that answered. */
    showNavIcon: boolean;
    /** Handshake and session: setting on, desktop, and Default nav mode. */
    sessionEnabled: boolean;
};

/**
 * Shared gates for the Settings toggle, Default nav icon, and the driver session.
 *
 * The setting is per-origin. Without `desktopAvailable`, a leftover localStorage true would paint the
 * glyph on a phone or a scene whose host flag is off — with no toggle to turn it off.
 *
 * Only the icon waits on the handshake; gating `sessionEnabled` on it would deadlock.
 */
export function useSpaceMouseAccess(): SpaceMouseAccess {
    const settingOn = useFeatureToggle(FeatureToggle.SpaceMouseEnabled);
    const available = useAppSelector(selectIsSpaceMouseAvailable);
    const isMobileDesign = useAppSelector(selectIsProcoreMobileDesign);
    const isPhone = useIsPhone();
    const controller = useAppSelector(selectCameraDefaults).pinhole.controller;
    const driverConfirmed = useSpaceMouseDriverConfirmed();
    const desktopAvailable = available && !isMobileDesign && !isPhone;
    return {
        settingOn,
        desktopAvailable,
        showNavIcon: settingOn && desktopAvailable && driverConfirmed,
        sessionEnabled: settingOn && desktopAvailable && controller === "flight",
    };
}
